<template>
  <div id="project-details-create" class="professional-attachment-create">
    <v-card class="attachment-form-card" elevation="6">
      <!-- Professional Header -->
      <div class="attachment-header-section">
        <v-card-title class="professional-header-title pa-0">
          <div class="header-content">
            <div class="title-wrapper">
              <v-icon size="28" color="white" class="header-icon">mdi-plus-circle</v-icon>
              <div class="title-text">
                <h1 class="form-title">{{ pageTitle }}</h1>
                <p class="form-subtitle">Add new event with image details</p>
              </div>
            </div>
            <v-btn 
              icon 
              small
              @click="closePrompt"
              class="close-btn"
            >
              <v-icon size="20" color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
      </div>
      <!-- Form Content -->
      <v-card-text class="form-content-wrapper pa-8">
        <div class="loading-wrapper" v-if="LoadingFlag">
          <v-row justify="center" class="mb-3">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-row>
        </div>

        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          @submit.prevent="confirmAlert"
        >
          <v-container class="py-0">
            <!-- Row 1: Event Name & Date -->
            <v-row class="form-row">
              <v-col cols="12" sm="6">
                <div class="field-wrapper">
                  <label class="field-label">
                    <v-icon size="20" color="#1976d2" class="mr-2">mdi-tag</v-icon>
                    <span class="required-indicator">*</span> Event Name
                  </label>
                  <v-text-field
                    v-model="EventName"
                    placeholder="Enter event name"
                    :rules="EventNameRules"
                    required
                    outlined
                    dense
                    class="mt-3"
                    prepend-inner-icon="mdi-text"
                  />
                </div>
              </v-col>

              <v-col cols="12" sm="6">
                <div class="field-wrapper">
                  <label class="field-label">
                    <v-icon size="20" color="#1976d2" class="mr-2">mdi-calendar</v-icon>
                    <span class="required-indicator">*</span> Event Date
                  </label>
                  <v-dialog
                    ref="dialog"
                    v-model="DateModal"
                    :return-value.sync="EventDate"
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="EventDate"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        :rules="EventDateRules"
                        v-on="on"
                        outlined
                        dense
                        placeholder="Select date"
                      />
                    </template>
                    <v-date-picker v-model="EventDate" scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="DateModal = false"
                        >Cancel</v-btn
                      >
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.dialog.save(EventDate)"
                        >OK</v-btn
                      >
                    </v-date-picker>
                  </v-dialog>
                </div>
              </v-col>
            </v-row>

            <!-- Row 2: Event Description -->
            <v-row class="form-row">
              <v-col cols="12">
                <div class="field-wrapper">
                  <label class="field-label">
                    <v-icon size="20" color="#1976d2" class="mr-2">mdi-note-text</v-icon>
                    <span class="required-indicator">*</span> Event Description
                  </label>
                  <v-textarea
                    v-model="EventDescription"
                    :rules="EventDescriptionRules"
                    outlined
                    auto-grow
                    dense
                    placeholder="Describe the event in detail"
                    rows="3"
                    prepend-inner-icon="mdi-text"
                  />
                </div>
              </v-col>
            </v-row>

            <!-- Row 3: Image Upload & Preview -->
            <v-row class="form-row">
              <v-col cols="12" md="5">
                <div class="field-wrapper">
                  <label class="field-label">
                    <v-icon size="20" color="#1976d2" class="mr-2">mdi-image-plus</v-icon>
                    <span class="required-indicator">*</span> Upload Event Image
                  </label>
                  <small class="upload-hint">JPG, PNG files only • Max 2MB</small>
                  <v-file-input
                    :clearable="false"
                    truncate-length="25"
                    accept="image/png, image/jpeg, image/jpg"
                    placeholder="Upload Event Photo"
                    prepend-icon="mdi-cloud-upload"
                    v-model="EventImage.UploadedImage"
                    :rules="EventImageRules"
                    @change="addImage(1)"
                    outlined
                    dense
                    class="mt-2"
                  />
                </div>
              </v-col>

              <v-col cols="12" md="7" class="d-flex flex-column">
                <label class="field-label">
                  <v-icon size="20" color="#1976d2" class="mr-2">mdi-image</v-icon>
                  Image Preview
                </label>
                <v-card class="preview-card" elevation="3">
                  <div v-if="EventImage.ImageUrl" class="preview-image-wrapper">
                    <img 
                      :src="EventImage.ImageUrl" 
                      alt="Event Preview"
                      class="preview-image"
                    />
                    <div class="preview-actions">
                      <v-btn
                        icon
                        color="white"
                        small
                        class="delete-preview-btn"
                        @click="deleteImage(1)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <div v-else class="preview-empty">
                    <v-icon size="48" color="grey lighten-1"
                      >mdi-image-off</v-icon
                    >
                    <div class="grey--text mt-2">No image uploaded</div>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Action Buttons -->
            <v-row class="form-row mt-6">
              <v-col cols="12" class="text-center">
                <v-btn
                  @click="closePrompt"
                  elevation="2"
                  rounded
                  color="grey lighten-2"
                  class="cancel-btn mr-4"
                >
                  <v-icon left size="18">mdi-close</v-icon>
                  Cancel
                </v-btn>
                <v-btn
                  :disabled="!valid1"
                  @click.prevent="addEvent"
                  :loading="SubmitFlag"
                  elevation="6"
                  rounded
                  color="#1976d2"
                  class="submit-btn white--text"
                >
                  <v-icon left size="18">mdi-plus-circle</v-icon>
                  Add Event
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    addRecordPrompt: {
      required: true,
      type: Boolean,
    },
    pageTitle: {
      required: true,
    },
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SubmitFlag: false,
      SearchFlag: false,

      UploadFlag: false,
      ResultFlag: false,

      addButtonFlag: true,

      eventData: "",
      Project: {},

      // EventDate: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      EventDate: "",
      EventDateRules: [(v) => !!v || "Event Date is required"],
      DateModal: false,

      EventName: "",
      EventNameRules: [(v) => !!v || "Event Name is required"],

      EventDescription: "",
      EventDescriptionRules: [(v) => !!v || "Event Description is required"],

      EventImage: {},
      EventImageRules: [value => !value || value.size < 2000000 || 'Event Image should be less than 2 MB!',],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;
      }
    },
  },
  mounted() {},
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      this.LoadingFlag = false;
    },
    addImage(id) {
      console.log("addImage called");
      console.log({ id });
      switch (id) {
        case 1:
          var file = this.EventImage.UploadedImage;
          console.log({ file });
          var src = URL.createObjectURL(file);
          console.log({ src });
          if (this.EventImage.src) {
            URL.revokeObjectURL(this.EventImage.src);
          }
          var FileType = file.type;
          console.log({ FileType });
          this.EventImage = {
            ImageType: FileType,
            ImageUrl: src,
            UploadedImage: file,
          };
          break;

        default:
          break;
      }
    },
    deleteImage(id) {
      console.log("deleteImage called");
      console.log({ id });
      switch (id) {
        case 1:
          this.EventImage = {
            ImageType: null,
            ImageUrl: null,
            UploadedImage: null,
          };
          break;

        default:
          break;
      }
    },
    closePrompt() {
      console.log("closePrompt called");

      this.Project = {
        EventName: "",
        EventDate: "",
        EventDescription: "",
        EventImageName: "",
        EventImageUrl: "",
        EventImage: {},
      };

      this.$emit("hideAddRecordPrompt", this.Project);
    },
    resetForm() {
      console.log("resetForm called");
      this.EventName = "";
      this.EventDate = "";
      this.EventDescription = "";
      this.EventImage = {};
    },
    addEvent() {
      console.log("addEvent called");

      var validate1 = this.$refs.form1.validate();
      console.log({ validate1 });

      console.log("EventImage = ", this.EventImage);

      var eventImageUrl =
        this.EventImage.ImageUrl == null || undefined
          ? ""
          : this.EventImage.ImageUrl;
      var eventImageName =
        this.EventImage.UploadedImage.name == null || undefined
          ? ""
          : this.EventImage.UploadedImage.name;

      console.log({ eventImageUrl }, { eventImageName });

      if (validate1 && eventImageName != "") {
        this.Project = {
          EventName: this.EventName,
          EventDate: this.EventDate,
          EventDescription: this.EventDescription,
          EventImageName: eventImageName,
          EventImageUrl: eventImageUrl,
          EventImage: this.EventImage,
        };

        console.log("Project = ", JSON.stringify(this.Project));

        this.$emit("hideAddRecordPrompt", this.Project);
        this.resetForm();
      } else {
        var message = "";
        if (!validate1) {
          message = "Kindly fill all the fields";
        }
        if (this.EventImage == "") {
          message = "Kindly Upload Event Image";
        }
        this.sweetAlert("error", message, false);
      }
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        // this.toast("error", "Kindly fill the required fields", true);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss" scoped>
/* Professional Attachment Create Form - AttachmentCreate.vue */

// Color variables
$primary-blue: #1976d2;
$primary-dark: #1565c0;
$grey-light: #f5f5f5;
$grey-border: #e0e0e0;
$grey-text: #666;
$grey-muted: #999;
$white: #ffffff;

#project-details-create {
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);

  // =============================================
  // HEADER STYLING (Professional Title Section)
  // =============================================
  .attachment-header-section {
    margin: 0;
    
    .professional-header-title {
      background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
      color: white;
      padding: 1rem 1.5rem !important;
      min-height: auto;

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        gap: 1.5rem;

        .title-wrapper {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;

          .header-icon {
            flex-shrink: 0;
            min-width: 28px;
          }

          .title-text {
            display: flex;
            flex-direction: column;
            gap: 0.15rem;

            .form-title {
              font-size: 1.4rem;
              font-weight: 700;
              line-height: 1.2;
              margin: 0;
              color: white;
              letter-spacing: 0.2px;
            }

            .form-subtitle {
              font-size: 0.8rem;
              font-weight: 500;
              margin: 0;
              color: rgba(255, 255, 255, 0.9);
              letter-spacing: 0.1px;
            }
          }
        }

        .close-btn {
          flex-shrink: 0;
          background: rgba(255, 255, 255, 0.15) !important;
          border-radius: 4px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          min-width: 36px;
          height: 36px;

          &:hover {
            background: rgba(255, 255, 255, 0.25) !important;
            transform: rotate(90deg);
          }

          .v-icon {
            color: white !important;
          }
        }
      }
    }
  }

  // =============================================
  // FORM CONTENT SECTION
  // =============================================
  .form-content-wrapper {
    padding: 2rem !important;

    // Loading state
    .loading-wrapper {
      padding: 1.5rem 0;
      text-align: center;
    }

    // Form row spacing
    .form-row {
      margin-bottom: 1.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    // =============================================
    // FIELD STYLING
    // =============================================
    .field-wrapper {
      position: relative;

      .field-label {
        display: flex;
        align-items: center;
        font-weight: 700;
        font-size: 0.95rem;
        color: #1a1a1a;
        margin-bottom: 0.75rem;
        letter-spacing: 0.3px;

        .v-icon {
          flex-shrink: 0;
        }

        .required-indicator {
          color: #f44336;
          font-weight: 800;
          margin-right: 0.25rem;
        }
      }

      .upload-hint {
        display: block;
        font-size: 0.8rem;
        color: #999;
        margin-top: 0.25rem;
        font-weight: 500;
      }

      // Text Input Fields
      .v-text-field {
        &.v-input--is-focused {
          .v-input__slot {
            fieldset {
              border-color: $primary-blue !important;
              border-width: 2px;
            }
          }
        }

        .v-input__slot {
          background: white;
          border-radius: 6px;

          fieldset {
            border-color: $grey-border;
            border-width: 1px;
            transition: all 0.3s ease;
          }

          .v-input__prepend-inner {
            .v-icon {
              color: $primary-blue;
              font-weight: 500;
            }
          }
        }

        .v-input__control {
          .v-input__slot input {
            color: #1a1a1a;
            font-weight: 500;
            font-size: 0.95rem;

            &::placeholder {
              color: #bbb;
              font-weight: 400;
            }
          }
        }
      }

      // Textarea Fields
      .v-textarea {
        &.v-input--is-focused {
          .v-input__slot {
            fieldset {
              border-color: $primary-blue !important;
              border-width: 2px;
            }
          }
        }

        .v-input__slot {
          background: white;
          border-radius: 6px;
          padding: 0 !important;

          fieldset {
            border-color: $grey-border;
            border-width: 1px;
            transition: all 0.3s ease;
          }

          .v-input__prepend-inner {
            padding: 12px 8px 0 12px;

            .v-icon {
              color: $primary-blue;
            }
          }

          textarea {
            color: #1a1a1a;
            font-weight: 500;
            padding: 12px 12px !important;
            font-size: 0.95rem;

            &::placeholder {
              color: #bbb;
              font-weight: 400;
            }
          }
        }
      }

      // File Input
      .v-file-input {
        .v-input__slot {
          background: white;
          border-radius: 6px;

          fieldset {
            border-color: $grey-border;
            border-width: 1px;
            transition: all 0.3s ease;
          }

          .v-input__prepend-inner {
            .v-icon {
              color: $primary-blue;
            }
          }

          .v-file-input__text {
            color: #666;
            font-weight: 500;
            font-size: 0.9rem;
          }

          .v-file-input__placeholder {
            color: #bbb;
          }
        }

        &.v-input--is-focused {
          .v-input__slot {
            fieldset {
              border-color: $primary-blue !important;
              border-width: 2px;
            }
          }
        }
      }

      // Date Picker Dialog
      .v-dialog {
        .v-date-picker {
          background: white;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
          border-radius: 8px;
        }
      }
    }

    // =============================================
    // IMAGE PREVIEW CARD
    // =============================================
    .preview-card {
      width: 100%;
      min-height: 220px;
      border: 2px solid $grey-border;
      border-radius: 8px;
      background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;

      &:hover {
        border-color: $primary-blue;
        box-shadow: 0 4px 16px rgba(25, 118, 210, 0.12);
      }

      .preview-image-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        border-radius: 6px;

        .preview-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          transition: transform 0.3s ease;

          &:hover {
            transform: scale(1.05);
          }
        }

        .preview-actions {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          transition: all 0.3s ease;
          opacity: 0;

          &:hover {
            background: rgba(0, 0, 0, 0.4);
            opacity: 1;
          }

          .delete-preview-btn {
            background: rgba(244, 67, 54, 0.8);
            transition: all 0.3s ease;

            &:hover {
              background: rgba(244, 67, 54, 1);
              transform: scale(1.1);
            }

            .v-icon {
              color: white !important;
            }
          }
        }
      }

      .preview-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        text-align: center;
        color: $grey-muted;

        .v-icon {
          margin-bottom: 1rem;
          opacity: 0.6;
        }

        div {
          font-size: 0.9rem;
          font-weight: 500;
        }
      }
    }

    // =============================================
    // ACTION BUTTONS
    // =============================================
    .cancel-btn {
      min-width: 140px;
      height: 44px;
      font-size: 0.95rem;
      font-weight: 700;
      letter-spacing: 0.3px;
      border-radius: 6px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      .v-icon {
        margin-right: 0.5rem;
      }

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
      }
    }

    .submit-btn {
      min-width: 140px;
      height: 44px;
      font-size: 0.95rem;
      font-weight: 700;
      letter-spacing: 0.3px;
      border-radius: 6px;
      background: linear-gradient(135deg, $primary-blue 0%, $primary-dark 100%);
      position: relative;
      overflow: hidden;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      .v-icon {
        margin-right: 0.5rem;
      }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left 0.3s ease;
      }

      &:hover:not(:disabled) {
        box-shadow: 0 6px 20px rgba(25, 118, 210, 0.35);
        transform: translateY(-2px);

        &::before {
          left: 100%;
        }
      }

      &:disabled {
        background: #ccc;
        color: #666;
        cursor: not-allowed;
        opacity: 0.6;
      }

      &.v-progress-circular--indeterminate {
        .v-icon {
          display: none;
        }
      }
    }
  }

  // =============================================
  // VALIDATION MESSAGES
  // =============================================
  .v-text-field__details,
  .v-textarea__details,
  .v-file-input__details {
    padding: 4px 12px 0 12px;

    .v-messages__message {
      font-size: 0.8rem;
      font-weight: 500;
      color: #f44336;
    }
  }

  // =============================================
  // RESPONSIVE DESIGN
  // =============================================
  @media (max-width: 960px) {
    .form-content-wrapper {
      padding: 1.5rem !important;

      .form-row {
        margin-bottom: 1.25rem;
      }

      .preview-card {
        min-height: 180px;
      }
    }

    .attachment-header-section {
      .professional-header-title {
        padding: 1.25rem !important;

        .header-content {
          .title-wrapper {
            .form-title {
              font-size: 1.5rem;
            }

            .form-subtitle {
              font-size: 0.8rem;
            }
          }
        }
      }
    }
  }

  @media (max-width: 600px) {
    .form-content-wrapper {
      padding: 1rem !important;

      .form-row {
        margin-bottom: 1rem;
      }

      .preview-card {
        min-height: 150px;
      }

      .cancel-btn,
      .submit-btn {
        width: 100%;
        min-width: auto;
        margin-bottom: 0.75rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    .attachment-header-section {
      .professional-header-title {
        padding: 1rem !important;

        .header-content {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.75rem;

          .close-btn {
            align-self: flex-end;
          }

          .title-wrapper {
            width: 100%;

            .form-title {
              font-size: 1.25rem;
            }

            .form-subtitle {
              font-size: 0.75rem;
            }
          }
        }
      }
    }
  }

  // =============================================
  // ANIMATIONS
  // =============================================
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .form-content-wrapper {
    animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  // Smooth input focus transitions
  .v-input--is-focused {
    .v-input__slot {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }
}
</style>